import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase, connectSupabase } from './supabase';
import { hasEverLoggedIn, markAsLoggedIn } from '../utils/firstTimeUser';
import { Platform } from 'react-native';
import type { Profile } from './database.client.types';
import { canAccessMainApp } from '../utils/authHelpers';
import {
    getAuthMessageLocaleMetadata,
    getDetectedClientPreferences,
    getDeviceFingerprintSeed,
} from './clientSessionMetadata';
import { deferSupabaseAuthCallbackWork } from './deferSupabaseAuthCallbackWork';
import {
    clearCurrentEntitlementQueries,
    shouldClearEntitlementsForAuthEvent,
} from './currentEntitlementsQueryCache';
import { invalidateEntitlementSnapshotCache } from './entitlements';
import {
    buildPersistedProductLanguageAuthority,
    canActivatePersistedProductLanguage,
    canonicalizeLocaleTag,
    isRuntimeLocale,
    matchLiveMessageLocaleTag,
} from './i18n/locales';
import {
    compareAndSetProfileLanguagePreference,
    createAuthLanguageMetadataWriteQueue,
    type ProfileLanguagePreferenceClient,
} from './i18n/profileLanguagePreference';

export type PreferredLanguageUpdateResult =
    | { ok: true; preferredLanguageTag: string }
    | { ok: false; reason: 'not-authenticated' | 'profile-unavailable' | 'unsupported' | 'conflict' | 'write-failed' };

type SessionContextType = {
    session: Session | null;
    profile: Profile | null;
    isLoading: boolean;
    isProfileLoading: boolean;
    isProfileResolved: boolean;
    profileLoadFailed: boolean;
    isReturningUser: boolean;
    markUserAsLoggedIn: () => Promise<void>;
    refreshSession: () => Promise<Session | null>;
    refreshProfile: () => Promise<void>;
    setPreferredLanguageTag: (languageTag: string) => Promise<PreferredLanguageUpdateResult>;
};

const SessionContext = createContext<SessionContextType | undefined>(undefined);

interface SessionProviderProps {
    children: ReactNode;
}

// Check if we're in static generation mode
// During SSG: executionEnvironment is 'bare' and we're on web without window
const isStaticGeneration = Platform.OS === 'web' && typeof window === 'undefined';
console.log('🔍 [SessionContext] Environment check:', {
    'Platform.OS': Platform.OS,
    'typeof window': typeof window,
    'isStaticGeneration': isStaticGeneration
});

function hasPersistedSupabaseSessionHint(): boolean {
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
        return false;
    }

    try {
        const storage = window.localStorage;
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (!key || !key.startsWith('sb-') || !key.includes('auth-token')) {
                continue;
            }

            const rawValue = storage.getItem(key);
            if (!rawValue || rawValue === 'null' || rawValue === '{}') {
                continue;
            }

            try {
                const parsed = JSON.parse(rawValue);
                if (Array.isArray(parsed) && parsed.some((entry) => entry?.access_token)) {
                    return true;
                }
                if (parsed?.access_token) {
                    return true;
                }
                if (parsed?.currentSession?.access_token) {
                    return true;
                }
            } catch {
                // Non-JSON values are unlikely, but regex fallback is safe.
                if (/"access_token"\s*:\s*"[^"]+"/.test(rawValue)) {
                    return true;
                }
            }
        }
    } catch {
        return false;
    }

    return false;
}

export function SessionProvider({ children }: SessionProviderProps) {
    const queryClient = useQueryClient();
    const [session, setSession] = useState<Session | null>(null);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [isProfileLoading, setIsProfileLoading] = useState(false);
    const [profileLoadFailed, setProfileLoadFailed] = useState(false);
    const [profileResolvedUserId, setProfileResolvedUserId] = useState<string | null>(null);
    const [profileRefreshCounter, setProfileRefreshCounter] = useState(0);
    // Keep server and client initial auth state aligned to avoid hydration mismatch.
    // This also ensures app-route static HTML renders a neutral boot screen instead of the sign-in gate.
    const initialLoading = true;
    console.log('🔍 [SessionContext] Initial state:', {
        'isStaticGeneration': isStaticGeneration,
        'initialLoading': initialLoading
    });
    const [isLoading, setIsLoading] = useState(initialLoading);
    const [isReturningUser, setIsReturningUser] = useState(false);
    const activeSessionUserIdRef = useRef<string | null>(null);
    activeSessionUserIdRef.current = session?.user?.id ?? null;
    const authLanguageMetadataQueueRef = useRef<ReturnType<
        typeof createAuthLanguageMetadataWriteQueue
    > | null>(null);
    if (!authLanguageMetadataQueueRef.current) {
        authLanguageMetadataQueueRef.current = createAuthLanguageMetadataWriteQueue({
            isCurrentUser: (userId) => activeSessionUserIdRef.current === userId,
            write: async (_userId, metadata) => {
                const { error } = await supabase.auth.updateUser({ data: metadata });
                if (error) throw error;
            },
        });
    }
    const authLanguageMetadataQueue = authLanguageMetadataQueueRef.current;

    // Track if auth setup has been attempted (for hydration handling)
    const [authSetupAttempted, setAuthSetupAttempted] = useState(false);

    // Hydration effect - ensure auth setup happens on client even after SSG
    useEffect(() => {
        // If we were initially in static generation but now have window,
        // we've hydrated and need to start loading
        if (isStaticGeneration && typeof window !== 'undefined' && !authSetupAttempted) {
            console.log('🔍 [SessionContext] Hydrated from SSG, starting auth setup...');
            setIsLoading(true);
            setAuthSetupAttempted(true);
        }
    }, [authSetupAttempted]);

    useEffect(() => {
        console.log('🔍 [SessionContext] useEffect running, isStaticGeneration:', isStaticGeneration);

        if (isStaticGeneration && typeof window === 'undefined') {
            console.log('🔍 [SessionContext] Skipping auth checks due to static generation (no window)');
            return; // no listener during SSG
        }

        if (authSetupAttempted) {
            console.log('🔍 [SessionContext] Auth setup already attempted, skipping');
            return; // Prevent duplicate setup
        }

        // Mark that we're attempting auth setup
        setAuthSetupAttempted(true);

        // 1) Ensure client exists
        console.log('🔍 [SessionContext] Ensuring Supabase connection…');
        try {
            connectSupabase?.();
        } catch (error) {
            console.error('🔍 [SessionContext] Failed to connect Supabase:', error);
            setIsLoading(false);
            return;
        }

        // Log environment details (useful for debugging)
        console.log('[env]', {
            isBrowser: typeof window !== 'undefined',
            hasLocalStorage: (() => {
                try {
                    if (Platform.OS !== 'web') return true; // Assume native storage works via Supabase adapter
                    localStorage.setItem('_', '_'); localStorage.removeItem('_'); return true
                } catch { return false }
            })(),
            supabaseUrl: process.env.EXPO_PUBLIC_SUPABASE_URL
        })

        // 2) Set up the auth state listener.
        // We rely solely on this listener to fetch the initial session and handle updates.
        // This avoids the race condition with getSession().
        console.log('🔐 [SessionContext] Setting up auth state listener…');
        const persistedSessionHint = hasPersistedSupabaseSessionHint();
        console.log('🔐 [SessionContext] Persisted session hint:', persistedSessionHint);

        // We use a flag within the effect scope to ensure setIsLoading(false) only runs once.
        let initialLoadComplete = false;
        let entitlementUserId: string | null | undefined;
        let initialNullSessionObserved = false;
        let nullSessionGraceTimeoutId: ReturnType<typeof setTimeout> | null = null;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const clearNullSessionGraceTimeout = () => {
            if (nullSessionGraceTimeoutId) {
                clearTimeout(nullSessionGraceTimeoutId);
                nullSessionGraceTimeoutId = null;
            }
        };

        const completeInitialLoad = (source: string) => {
            if (initialLoadComplete) {
                return;
            }

            initialLoadComplete = true;
            setIsLoading(false);
            clearNullSessionGraceTimeout();

            if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            console.log(`✅ [SessionContext] Initial load complete via ${source}.`);
        };

        const scheduleNullSessionGraceCompletion = (reason: string) => {
            if (initialLoadComplete || nullSessionGraceTimeoutId) {
                return;
            }

            const graceMs = persistedSessionHint ? 2500 : 1200;
            console.log(`🔐 [SessionContext] ${reason}. Waiting ${graceMs}ms for a follow-up auth event...`);
            nullSessionGraceTimeoutId = setTimeout(async () => {
                nullSessionGraceTimeoutId = null;
                const { data: { session }, error } = await supabase.auth.getSession();
                if (error) {
                    console.error('🔐 [SessionContext] Null-session grace getSession error:', error);
                }

                if (session) {
                    setSession(session);
                    completeInitialLoad('null-session grace getSession recovered');
                    return;
                }

                if (persistedSessionHint) {
                    // Keep the boot screen until the hard timeout fallback to avoid showing
                    // a false unauthenticated gate while a persisted session is still restoring.
                    console.log('🔐 [SessionContext] Persisted session hint present; waiting for timeout fallback before showing gate.');
                    return;
                }

                completeInitialLoad('null-session grace timeout');
            }, graceMs);
        };

        // Timeout fallback to prevent infinite loading
        timeoutId = setTimeout(() => {
            if (!initialLoadComplete) {
                console.warn('⚠️ [SessionContext] Auth listener timeout - checking session directly before completing');

                // Try to get session directly as fallback, then complete loading.
                // This avoids briefly exposing protected routes as unauthenticated
                // before we know whether a valid session exists.
                supabase.auth.getSession().then(({ data: { session }, error }) => {
                    if (error) {
                        console.error('🔐 [SessionContext] Fallback getSession error:', error);
                    } else {
                        console.log('🔐 [SessionContext] Fallback getSession result:', session ? 'Session exists' : 'No session');
                        setSession(session);
                    }
                }).finally(() => {
                    if (!initialLoadComplete) {
                        completeInitialLoad('timeout fallback');
                    }
                });
            }
        }, 5000); // 5 second timeout

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            console.log('🔐 [SessionContext] Auth event:', event);
            console.log('🔐 [SessionContext] Event session:', session ? 'Session exists' : 'No session');

            if (session) {
                console.log('  - user.email:', session.user?.email);
                console.log('  - user.id:', session.user?.id);
                console.log('  - user_metadata:', session.user?.user_metadata);
                console.log('  - first_name:', session.user?.user_metadata?.first_name);
            }

            const nextEntitlementUserId = session?.user?.id ?? null;
            const accountChanged = entitlementUserId !== undefined
                && entitlementUserId !== nextEntitlementUserId;
            if (shouldClearEntitlementsForAuthEvent(event) || accountChanged) {
                invalidateEntitlementSnapshotCache();
                clearCurrentEntitlementQueries(queryClient);
            }
            entitlementUserId = nextEntitlementUserId;

            // Update local state on every auth transition
            setSession(session ?? null);

            // Set realtime auth globally for all subscriptions
            if (session?.access_token) {
                const accessToken = session.access_token;
                deferSupabaseAuthCallbackWork('session realtime auth update', () => {
                    supabase.realtime.setAuth(accessToken);
                    console.log('🔐 [SessionContext] Updated realtime auth with new token');
                });
            }

            // Treat any initial null-session event carefully: some environments briefly emit
            // null events (INITIAL_SESSION, SIGNED_OUT, etc.) before restoring persisted sessions.
            if (!initialLoadComplete) {
                if (!session) {
                    initialNullSessionObserved = true;
                    scheduleNullSessionGraceCompletion(`Received ${event} with no session`);
                } else {
                    completeInitialLoad('auth listener');
                }
            }

            // Check for digest conversion (only on SIGNED_IN event)
            if (event === 'SIGNED_IN' && session && session.user.user_metadata?.conversion_type === 'digest') {
                console.log('🔐 [SessionContext] Digest conversion detected on sign in');
                console.log('🔐 [SessionContext] NOTE: This should have been handled by AuthContext already');
                const projectId = session.user.user_metadata.project_id;

                if (projectId) {
                    console.log('🔐 [SessionContext] Project ID available:', projectId);
                    console.log('🔐 [SessionContext] Would redirect to dashboard with project:', projectId);
                } else {
                    console.log('🔐 [SessionContext] No project ID found in user metadata');
                    console.log('🔐 [SessionContext] Would redirect to dashboard home');
                }
                // NOTE: No actual redirects here - handled by AuthContext
            }

        });

        // Immediate getSession() call as backup
        console.log('🔐 [SessionContext] Making immediate getSession() call as backup...');
        supabase.auth.getSession().then(({ data: { session }, error }) => {
            if (error) {
                console.error('🔐 [SessionContext] Immediate getSession error:', error);
            } else {
                console.log('🔐 [SessionContext] Immediate getSession result:', session ? 'Session exists' : 'No session');
                // Only finalize early if we found a valid session.
                // If null, wait for auth listener or timeout fallback to avoid false negatives.
                if (!initialLoadComplete && session) {
                    setSession(session);
                    completeInitialLoad('immediate getSession (session found)');
                } else if (!initialLoadComplete) {
                    if (initialNullSessionObserved) {
                        scheduleNullSessionGraceCompletion('Immediate getSession also returned no session after INITIAL_SESSION');
                    } else {
                        scheduleNullSessionGraceCompletion('Immediate getSession returned no session before auth listener settled');
                    }
                }
            }
        });

        // 3) Cleanup on unmount
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
            clearNullSessionGraceTimeout();
            subscription.unsubscribe();
        };
    }, [queryClient]);

    // This effect handles the isReturningUser logic
    useEffect(() => {
        // Skip during static generation or while still loading the initial session
        if (isStaticGeneration || isLoading) {
            return;
        }

        const checkUserHistory = async () => {
            if (!session) {
                // User is confirmed logged out, check if they have logged in before
                const hasLoggedIn = await hasEverLoggedIn();
                setIsReturningUser(hasLoggedIn);
            } else {
                // If they are logged in, they are inherently a returning user in this context
                setIsReturningUser(true);
            }
        };
        checkUserHistory();
        // Depend on isLoading to ensure this runs only after the session state is finalized
    }, [session, isLoading, isStaticGeneration]);

    const profileUserId = canAccessMainApp(session) ? session?.user?.id || null : null;

    useEffect(() => {
        let cancelled = false;

        const loadProfile = async () => {
            if (!profileUserId) {
                setProfile(null);
                setProfileResolvedUserId(null);
                setIsProfileLoading(false);
                setProfileLoadFailed(false);
                return;
            }

            setProfile((currentProfile) => (
                currentProfile?.id === profileUserId ? currentProfile : null
            ));
            setProfileResolvedUserId(null);
            setIsProfileLoading(true);
            setProfileLoadFailed(false);

            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', profileUserId)
                .maybeSingle();

            if (cancelled) {
                return;
            }

            if (error) {
                console.error('🔐 [SessionContext] Failed to fetch profile:', error);
                setProfile(null);
                setProfileResolvedUserId(profileUserId);
                setIsProfileLoading(false);
                setProfileLoadFailed(true);
                return;
            }

            setProfile(data as Profile | null);
            setProfileResolvedUserId(profileUserId);
            setIsProfileLoading(false);
            setProfileLoadFailed(false);
        };

        void loadProfile();

        return () => {
            cancelled = true;
        };
    }, [profileRefreshCounter, profileUserId]);

    useEffect(() => {
        const currentUserId = profileUserId;
        const currentSession = session;

        if (
            !currentUserId
            || !currentSession
            || !profile
            || profile.id !== currentUserId
        ) {
            return;
        }

        let cancelled = false;

        const syncProfileSessionContext = async () => {
            const detectedPreferences = getDetectedClientPreferences();
            const deviceFingerprint = getDeviceFingerprintSeed();
            const updatePayload: Record<string, unknown> = {
                last_active: new Date().toISOString(),
            };

            if (detectedPreferences.timezone) {
                updatePayload.timezone = detectedPreferences.timezone;
                updatePayload.detected_timezone = detectedPreferences.timezone;
            }

            if (deviceFingerprint) {
                updatePayload.device_fingerprint = deviceFingerprint;
            }

            if (currentSession.user.email) {
                updatePayload.email = currentSession.user.email;
            }

            if (detectedPreferences.primaryLocale) {
                updatePayload.detected_primary_locale = detectedPreferences.primaryLocale;
            }

            if (detectedPreferences.languageCode) {
                updatePayload.detected_language_code = detectedPreferences.languageCode;
            }

            if (detectedPreferences.regionCode) {
                updatePayload.detected_region_code = detectedPreferences.regionCode;
            }

            if (detectedPreferences.localeTags.length > 0) {
                updatePayload.detected_locale_tags = detectedPreferences.localeTags;
            }

            updatePayload.detected_preferred_currency = detectedPreferences.preferredCurrency;

            if (detectedPreferences.measurementSystem) {
                updatePayload.detected_measurement_system = detectedPreferences.measurementSystem;
            }

            if (detectedPreferences.uses24hourClock !== null) {
                updatePayload.detected_uses_24hour_clock = detectedPreferences.uses24hourClock;
            }

            if (detectedPreferences.firstWeekday !== null) {
                updatePayload.detected_first_weekday = detectedPreferences.firstWeekday;
            }

            const authLocaleMetadata = {
                ...getAuthMessageLocaleMetadata(profile.preferred_language_tag),
                preferred_language_tag: profile.preferred_language_tag,
                paperzilla_product_language_authority:
                    buildPersistedProductLanguageAuthority(profile.preferred_language_tag),
            };
            if (
                currentSession.user.user_metadata?.paperzilla_auth_message_locale
                !== authLocaleMetadata.paperzilla_auth_message_locale
                || currentSession.user.user_metadata?.preferred_language_tag
                !== authLocaleMetadata.preferred_language_tag
                || currentSession.user.user_metadata?.paperzilla_product_language_authority
                !== authLocaleMetadata.paperzilla_product_language_authority
            ) {
                const authMetadataResult = await authLanguageMetadataQueue.enqueue(
                    currentUserId,
                    authLocaleMetadata
                );
                if (authMetadataResult.status === 'failed') {
                    console.warn(
                        '🔐 [SessionContext] Failed to sync Auth email locale metadata; English fallback remains active.'
                    );
                }
            }

            const { error } = await supabase
                .from('profiles')
                .update(updatePayload as any)
                .eq('id', currentUserId);

            if (cancelled) {
                return;
            }

            if (error) {
                console.error('🔐 [SessionContext] Failed to sync profile session context:', error);
                return;
            }

            setProfile((currentProfile) => {
                if (!currentProfile || currentProfile.id !== currentUserId) {
                    return currentProfile;
                }

                return {
                    ...currentProfile,
                    ...updatePayload,
                } as Profile;
            });
        };

        void syncProfileSessionContext();

        return () => {
            cancelled = true;
        };
    }, [authLanguageMetadataQueue, profile?.preferred_language_tag, profileUserId, session?.user?.email]);

    useEffect(() => {
        const sessionFirstName = session?.user?.user_metadata?.first_name;
        const normalizedFirstName = typeof sessionFirstName === 'string' ? sessionFirstName.trim() : '';

        if (!profileUserId || !canAccessMainApp(session) || isProfileLoading) {
            return;
        }

        if (!normalizedFirstName || profile?.first_name) {
            return;
        }

        let cancelled = false;

        const syncProfileFirstName = async () => {
            const { error } = await supabase
                .from('profiles')
                .update({ first_name: normalizedFirstName })
                .eq('id', profileUserId)
                .is('first_name', null);

            if (cancelled) {
                return;
            }

            if (error) {
                console.error('🔐 [SessionContext] Failed to sync profile first name from auth metadata:', error);
                return;
            }

            setProfile((currentProfile) => {
                if (!currentProfile || currentProfile.first_name) {
                    return currentProfile;
                }

                return {
                    ...currentProfile,
                    first_name: normalizedFirstName,
                };
            });
        };

        void syncProfileFirstName();

        return () => {
            cancelled = true;
        };
    }, [session, profile, profileUserId, isProfileLoading]);

    const refreshSession = useCallback(async (): Promise<Session | null> => {
        const { data: { session: refreshedSession }, error } = await supabase.auth.getSession();
        if (error) {
            throw error;
        }
        setSession(refreshedSession ?? null);
        return refreshedSession ?? null;
    }, []);

    const refreshProfile = useCallback(async () => {
        setProfileRefreshCounter((prev) => prev + 1);
    }, []);

    const setPreferredLanguageTag = useCallback(async (
        languageTag: string
    ): Promise<PreferredLanguageUpdateResult> => {
        if (!canActivatePersistedProductLanguage()) {
            return { ok: false, reason: 'unsupported' };
        }
        if (!profileUserId || !canAccessMainApp(session)) {
            return { ok: false, reason: 'not-authenticated' };
        }
        if (!profile || profile.id !== profileUserId) {
            return { ok: false, reason: 'profile-unavailable' };
        }

        const canonicalLanguageTag = canonicalizeLocaleTag(languageTag);
        if (
            !canonicalLanguageTag
            || !isRuntimeLocale(canonicalLanguageTag)
            || !matchLiveMessageLocaleTag(canonicalLanguageTag)
        ) {
            return { ok: false, reason: 'unsupported' };
        }
        if (profile.preferred_language_tag === canonicalLanguageTag) {
            return { ok: true, preferredLanguageTag: canonicalLanguageTag };
        }

        try {
            const result = await compareAndSetProfileLanguagePreference(
                supabase as unknown as ProfileLanguagePreferenceClient,
                profileUserId,
                profile.preferred_language_tag,
                canonicalLanguageTag
            );
            if (result.status === 'conflict') {
                return { ok: false, reason: 'conflict' };
            }

            setProfile((currentProfile) => {
                if (
                    !currentProfile
                    || currentProfile.id !== profileUserId
                    || currentProfile.preferred_language_tag !== profile.preferred_language_tag
                ) {
                    return currentProfile;
                }
                return {
                    ...currentProfile,
                    preferred_language_tag: canonicalLanguageTag,
                } as Profile;
            });

            const authLocaleMetadata = {
                ...getAuthMessageLocaleMetadata(canonicalLanguageTag),
                preferred_language_tag: canonicalLanguageTag,
                paperzilla_product_language_authority:
                    buildPersistedProductLanguageAuthority(canonicalLanguageTag),
            };
            const authMetadataResult = await authLanguageMetadataQueue.enqueue(
                profileUserId,
                authLocaleMetadata
            );
            if (authMetadataResult.status === 'failed') {
                console.warn(
                    '🔐 [SessionContext] Language saved, but Auth email locale metadata could not be refreshed.'
                );
            }

            return { ok: true, preferredLanguageTag: canonicalLanguageTag };
        } catch (error) {
            console.error('🔐 [SessionContext] Failed to update product language:', error);
            return { ok: false, reason: 'write-failed' };
        }
    }, [authLanguageMetadataQueue, profile, profileUserId, session]);

    const markUserAsLoggedIn = async () => {
        // Only run when not in static generation
        if (!isStaticGeneration) {
            await markAsLoggedIn();
            setIsReturningUser(true);
        }
    };

    const value = {
        session,
        profile,
        isLoading,
        isProfileLoading,
        isProfileResolved: !profileUserId || profileResolvedUserId === profileUserId,
        profileLoadFailed,
        isReturningUser,
        markUserAsLoggedIn,
        refreshSession,
        refreshProfile,
        setPreferredLanguageTag,
    };

    return (
        <SessionContext.Provider value={value}>
            {children}
        </SessionContext.Provider>
    );
}

export function useSession() {
    const context = useContext(SessionContext);
    if (context === undefined) {
        throw new Error('useSession must be used within a SessionProvider');
    }
    return context;
}
